<script lang="ts">
	import ChevronDownIcon from "@lucide/svelte/icons/chevron-down";
	import MenuIcon from "@lucide/svelte/icons/menu";
	import PhoneIcon from "@lucide/svelte/icons/phone";
	import { slide, fade } from "svelte/transition";
	import { page } from "$app/state";
	import { cn } from "#lib/web-ui/ui/utils.js";
	import { getTemplateContext, cloudinaryUrl } from "../template-context.svelte";
	import { titleCase } from "@fergmar/ui/strings";

	const templateContext = getTemplateContext();

	// Generate navigation links from servicedAreas
	const whereWeBuyLinks = templateContext.servicedAreas.map(({ city, state }) => ({
		label: `${titleCase(city)}, ${state.toUpperCase()}`,
		href: `/${state}/${city}/`
	}));

	const navigationMenu = [
		{
			label: "Process & Options",
			href: "/home-buying-options/",
			children: undefined
		},
		{
			label: "Where We Buy",
			href: "/where-we-buy/",
			children: [...whereWeBuyLinks]
		},
		{
			label: "Blog",
			href: "/blog/",
			children: undefined
		},
		{
			label: "About",
			href: "/about/",
			children: [
				{
					label: "Meet the Team",
					href: "/meet-the-team/"
				},
				{
					label: "FAQs",
					href: "/faqs/"
				},
				{
					label: "About Us",
					href: "/about/"
				},
				{
					label: "Referral Program",
					href: "/get-paid/"
				}
			]
		},
		{
			label: "Contact",
			href: "/contact/",
			children: undefined
		}
	];

	let mobileMenuOpen = $state(false);
	let activeMobileDropdown = $state<string | null>(null);
	let activeDesktopDropdown = $state<string | null>(null);

	function toggleMobileDropdown(label: string) {
		activeMobileDropdown = activeMobileDropdown === label ? null : label;
	}

	function closeMobileMenu() {
		mobileMenuOpen = false;
		activeMobileDropdown = null;
	}

	function openDesktopDropdown(label: string) {
		activeDesktopDropdown = label;
	}

	function closeDesktopDropdown() {
		activeDesktopDropdown = null;
	}
</script>

<header class="sticky top-0 z-50 w-full border-b bg-white">
	<div class="mx-auto max-w-7xl px-4 py-2 sm:px-6">
		<div class="flex flex-row items-center justify-between py-2">
			<div class="flex w-[200px] justify-start">
				<a href="/" class="flex items-center">
					<img
						src={cloudinaryUrl(templateContext.images.lightLogo, { width: 200 })}
						alt={templateContext.siteName}
						width="200"
						height="75"
						loading="eager"
						fetchpriority="high"
					/>
				</a>
			</div>

			<nav class="hidden items-center justify-center space-x-4 md:flex">
				{#each navigationMenu as item (item.label)}
					{#if item.children && item.children.length > 0}
						<div
							class="group relative"
							role="presentation"
							onmouseenter={() => openDesktopDropdown(item.label)}
							onmouseleave={closeDesktopDropdown}
						>
							<button
								type="button"
								class={cn(
									"hover:text-primary flex cursor-pointer items-center border-0 bg-transparent font-medium whitespace-nowrap text-gray-700 outline-none",
									(page.url.pathname === item.href ||
										item.children.some((c) => c.href === page.url.pathname)) &&
										"text-primary"
								)}
								onclick={() =>
									activeDesktopDropdown === item.label
										? closeDesktopDropdown()
										: openDesktopDropdown(item.label)}
								onkeydown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										activeDesktopDropdown === item.label
											? closeDesktopDropdown()
											: openDesktopDropdown(item.label);
									}
									if (
										e.key === "Escape" &&
										activeDesktopDropdown === item.label
									) {
										closeDesktopDropdown();
									}
								}}
								aria-expanded={activeDesktopDropdown === item.label}
								aria-haspopup="true"
							>
								<span>{item.label}</span>
								<ChevronDownIcon
									class="ml-1 h-4 w-4 transition-transform duration-200 {activeDesktopDropdown ===
									item.label
										? 'rotate-180'
										: ''}"
								/>
							</button>

							{#if activeDesktopDropdown === item.label}
								<div
									class="absolute top-full left-0 z-50 min-w-48 origin-top rounded-md border bg-white py-1 shadow-lg"
									role="menu"
									transition:fade={{ duration: 150 }}
								>
									{#each item.children as child (child.href)}
										<a
											href={child.href}
											role="menuitem"
											class="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
											class:text-primary={page.url.pathname === child.href}
										>
											{child.label}
										</a>
									{/each}
								</div>
							{/if}
						</div>
					{:else}
						<a
							href={item.href}
							class="hover:text-primary font-medium whitespace-nowrap text-gray-700"
							class:text-primary={page.url.pathname === item.href}
						>
							{item.label}
						</a>
					{/if}
				{/each}
			</nav>

			<div class="flex w-[200px] items-center justify-end gap-3 text-base">
				<div class="hidden md:block">
					<a
						href={`tel:${templateContext.contact.phone}`}
						class="bg-primary hover:bg-primary/80 focus-visible:ring-primary rounded-md px-5 py-2 text-lg font-semibold text-white shadow-md ring-1 ring-black/10 transition-all hover:shadow-lg focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
					>
						{templateContext.contact.phone}
					</a>
				</div>

				<a
					href={`tel:${templateContext.contact.phone}`}
					class="text-primary hover:text-primary/80 hover:bg-primary/10 focus-visible:ring-primary rounded-md p-2 font-semibold transition-all focus-visible:ring-2 focus-visible:outline-none md:hidden"
					aria-label="Call us at {templateContext.contact.phone}"
				>
					<PhoneIcon class="h-5 w-5 fill-current" />
				</a>

				<button
					type="button"
					class="text-gray-500 hover:text-gray-700 md:hidden"
					onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
					aria-label="Toggle mobile menu"
					aria-expanded={mobileMenuOpen}
					aria-controls="mobile-menu"
				>
					<MenuIcon class="h-6 w-6" />
				</button>
			</div>
		</div>
	</div>

	{#if mobileMenuOpen}
		<div
			class="fixed inset-0 z-40 bg-black/20 md:hidden"
			onclick={closeMobileMenu}
			transition:fade={{ duration: 200 }}
			aria-hidden="true"
		></div>
	{/if}

	{#if mobileMenuOpen}
		<div
			class="relative z-50 border-t bg-white md:hidden"
			transition:slide={{ duration: 200 }}
			id="mobile-menu"
		>
			<div class="max-w-8xl container mx-auto px-4">
				<nav class="flex flex-col" aria-label="Mobile navigation">
					{#each navigationMenu as item (item.label)}
						{#if item.children && item.children.length > 0}
							<div class="flex flex-col border-b">
								<button
									type="button"
									class="hover:text-primary flex items-center justify-between py-4 font-medium text-gray-700"
									class:text-primary={page.url.pathname === item.href}
									onclick={() => toggleMobileDropdown(item.label)}
									aria-expanded={activeMobileDropdown === item.label}
									aria-controls={`mobile-submenu-${item.label.toLowerCase().replace(/ /g, "-")}`}
								>
									{item.label}
									<ChevronDownIcon
										class="h-4 w-4 transition-transform duration-200 {activeMobileDropdown ===
										item.label
											? 'rotate-180'
											: ''}"
									/>
								</button>
								{#if activeMobileDropdown === item.label}
									<div
										class="ml-4 flex flex-col space-y-2 pb-4"
										transition:slide={{ duration: 200 }}
										id={`mobile-submenu-${item.label.toLowerCase().replace(/ /g, "-")}`}
										role="region"
										aria-label={`${item.label} submenu`}
									>
										{#each item.children as child (child.href)}
											<a
												href={child.href}
												class="hover:text-primary py-1 text-sm text-gray-600"
												class:text-primary={page.url.pathname ===
													child.href}
												onclick={closeMobileMenu}
											>
												{child.label}
											</a>
										{/each}
									</div>
								{/if}
							</div>
						{:else}
							<a
								href={item.href}
								class="hover:text-primary border-b py-4 font-medium text-gray-700"
								class:text-primary={page.url.pathname === item.href}
								onclick={closeMobileMenu}
							>
								{item.label}
							</a>
						{/if}
					{/each}
				</nav>
			</div>
		</div>
	{/if}
</header>
